import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { useAppState } from '../../context/AppStateContext';
import {
  ChildDocument,
  ChildDocumentDataService,
  ChildDocumentMetadata,
  DocumentType,
} from '../../services/childDocumentDataService';
import HelpAsk from '../../help/HelpAsk';
import ConfirmationModal from '../ConfirmationModal';
import PdfViewer from '../common/PdfViewer';
import './documents.scss';

interface Props {
  childId: string | null;
  childProfiles: ChildProfile[];
}

const fmtDate = (d: string | null | undefined): string =>
  d ? new Date(d).toLocaleDateString() : '—';

const fmtDateTime = (d: string | null | undefined): string =>
  d
    ? `${new Date(d).toLocaleDateString()}, ${new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '—';

const stripPdfExt = (name: string): string => name.replace(/\.pdf$/i, '');

// Map a type name to a badge accent. Unknown types fall back to neutral so new
// types added later still render cleanly.
const typeBadgeClass = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes('ehcp')) return 'cd-type-badge--ehcp';
  if (n.includes('referral')) return 'cd-type-badge--referral';
  return 'cd-type-badge--default';
};

const ChildDocuments: React.FC<Props> = ({ childId, childProfiles }) => {
  const { userRoleIds, roles } = useAppState();

  const isAdmin = userRoleIds.includes(roles.Admin);
  // Documents are view-only for everyone except Admins, who can download.
  const canDownload = isAdmin;

  const child = useMemo(
    () => childProfiles.find((c) => String(c.id) === childId) ?? null,
    [childProfiles, childId],
  );

  const [docs, setDocs] = useState<ChildDocument[]>([]);
  const [types, setTypes] = useState<DocumentType[]>([]);
  const [uploadTypeId, setUploadTypeId] = useState<number | undefined>(undefined);
  const [filterTypeId, setFilterTypeId] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<ChildDocument | null>(null);
  const [editDoc, setEditDoc] = useState<ChildDocument | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<ChildDocument | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load the document type lookup once; default the upload selector to 'Other'.
  useEffect(() => {
    ChildDocumentDataService.listTypes()
      .then((t) => {
        setTypes(t);
        setUploadTypeId((prev) => prev ?? t.find((x) => x.name === 'Other')?.id ?? t[0]?.id);
      })
      .catch((err) => console.error('Failed to load document types', err));
  }, []);

  const load = async (cid: number) => {
    setLoading(true);
    setError(null);
    try {
      setDocs(await ChildDocumentDataService.list(cid));
    } catch (err) {
      console.error('Failed to load documents', err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (childId) load(parseInt(childId, 10));
    else setDocs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || !child || !child.unique_id) return;
    const list = Array.from(files);
    const nonPdf = list.filter((f) => f.type !== 'application/pdf');
    if (nonPdf.length > 0) {
      setError('Only PDF files can be uploaded.');
      if (list.length === nonPdf.length) return;
    }
    setUploading(true);
    setError(null);
    try {
      for (const file of list.filter((f) => f.type === 'application/pdf')) {
        await ChildDocumentDataService.upload(
          { id: child.id, unique_id: child.unique_id! },
          file,
          stripPdfExt(file.name),
          uploadTypeId,
        );
      }
      await load(child.id);
    } catch (err) {
      console.error('Upload failed', err);
      setError('One or more uploads failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc: ChildDocument) => {
    try {
      const url = await ChildDocumentDataService.getDownloadUrl(doc.id);
      // Presigned S3 URLs ignore the `download` attribute on same-tab nav.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download failed', err);
      setError('Failed to download document.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDoc || !child) return;
    try {
      await ChildDocumentDataService.remove(deleteDoc.id);
      setDeleteDoc(null);
      await load(child.id);
    } catch (err) {
      console.error('Delete failed', err);
      setError('Failed to delete document.');
      setDeleteDoc(null);
    }
  };

  if (!childId || !child) {
    return (
      <div className="cd-placeholder">
        <i className="fas fa-folder-open" />
        <p>Pick a child from the sidebar to view their documents.</p>
      </div>
    );
  }

  const searchTerm = search.trim().toLowerCase();
  const visibleDocs = docs.filter((d) =>
    (filterTypeId === 'all' || d.document_type_id === filterTypeId) &&
    (searchTerm === '' || d.display_name.toLowerCase().includes(searchTerm))
  );

  return (
    <div className="child-documents">
      <div className="cd-head">
        <h3 className="cd-head__title">Documents — <span className="cd-head__child">{child.first_name} {child.last_name}</span></h3>
      </div>

      {/* Upload panel (Admin only) */}
      {isAdmin && (
        <div className="cd-upload-panel">
          <div className="cd-upload-panel__head">
            <span className="cd-upload-panel__title">Add a document</span>
            <span className="cd-upload-panel__hint">(PDF only)</span>
          </div>
          <div className="cd-upload-panel__row">
            <div className="cd-field" data-help="documents.upload-type">
              <label className="cd-field__label">Type</label>
              <select
                className="cd-select"
                value={uploadTypeId ?? ''}
                onChange={(e) => setUploadTypeId(Number(e.target.value))}
                disabled={uploading}
                title="Type applied to uploaded documents"
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFiles(e.target.files)}
            />

            <div
              className={`cd-dropzone${isDragging ? ' is-dragging' : ''}${uploading ? ' is-busy' : ''}`}
              data-help="documents.dropzone"
              onClick={() => !uploading && fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (!uploading) handleFiles(e.dataTransfer.files);
              }}
            >
              {uploading ? (
                <><span className="spinner-border spinner-border-sm" /><span>Uploading…</span></>
              ) : (
                <>
                  <i className="fas fa-cloud-arrow-up" />
                  <span>Drag a PDF here, or</span>
                  <span className="cd-btn cd-btn--primary"><i className="fas fa-file-lines" />Choose PDF</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {error && <div className="cd-alert"><i className="fas fa-triangle-exclamation" />{error}</div>}

      {(loading || docs.length > 0) && (
        <div className="cd-filter" data-help="documents.filter">
          <div className="cd-search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="cd-filter__label">Filter by type</label>
          <select
            className="cd-select"
            value={filterTypeId}
            onChange={(e) => setFilterTypeId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value="all">All types</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {!loading && (
            <span className="cd-filter__count">{docs.length} {docs.length === 1 ? 'document' : 'documents'}</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="cd-empty"><div className="loading-spinner" />Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="cd-empty">
          <i className="fas fa-folder-open cd-empty__icon" />
          <div>
            No documents held for {child.first_name} yet.
            <div className="mt-3">
              <HelpAsk guideKey="documents.open-document" label="How do documents work?" variant="inline" />
            </div>
            {/* An empty list is where an Admin most needs the upload guide, and the
                plain sentence it replaces could only point at the panel. */}
            {isAdmin && (
              <div className="mt-1">
                <HelpAsk guideKey="documents.add-document" label="How do I add one?" variant="inline" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="cd-table-wrap" data-help="documents.list">
          <table className="cd-table">
            <thead>
              <tr>
                <th className="cd-th">Name</th>
                <th className="cd-th">Type</th>
                <th className="cd-th">Document date</th>
                <th className="cd-th">Uploaded</th>
                <th className="cd-th">By</th>
                <th className="cd-th cd-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocs.map((doc) => (
                <tr key={doc.id} className="cd-row">
                  <td className="cd-td">
                    <button type="button" className="cd-name" onClick={() => setViewerDoc(doc)} title="View document">
                      <i className="fas fa-file-lines" />
                      <span className="cd-name__text">{doc.display_name}</span>
                    </button>
                  </td>
                  <td className="cd-td">
                    <span className={`cd-type-badge ${typeBadgeClass(doc.document_type_name)}`}>
                      {doc.document_type_name}
                    </span>
                  </td>
                  <td className="cd-td cd-td--muted">{fmtDate(doc.document_date)}</td>
                  <td className="cd-td cd-td--muted">{fmtDateTime(doc.uploaded_at)}</td>
                  <td className="cd-td">{doc.created_by_name ?? '—'}</td>
                  <td className="cd-td cd-td--right">
                    <span className="cd-actions">
                      <button type="button" className="cd-action cd-action--view" onClick={() => setViewerDoc(doc)} title="View">
                        <i className="fas fa-eye" />
                      </button>
                      {canDownload && (
                        <button type="button" className="cd-action cd-action--download" onClick={() => handleDownload(doc)} title="Download">
                          <i className="fas fa-download" />
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          <button type="button" className="cd-action cd-action--edit" onClick={() => setEditDoc(doc)} title="Edit details">
                            <i className="fas fa-pen" />
                          </button>
                          <button type="button" className="cd-action cd-action--delete" onClick={() => setDeleteDoc(doc)} title="Delete">
                            <i className="fas fa-trash" />
                          </button>
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
              {visibleDocs.length === 0 && (
                <tr>
                  <td className="cd-td cd-td--muted" colSpan={6} style={{ textAlign: 'center', padding: '28px' }}>
                    No documents match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewerDoc && (
        <PdfViewer
          sourceKey={viewerDoc.id}
          title={viewerDoc.display_name}
          load={async () => ({ url: await ChildDocumentDataService.getViewUrl(viewerDoc.id) })}
          onClose={() => setViewerDoc(null)}
        />
      )}

      {editDoc && (
        <EditDocumentModal
          doc={editDoc}
          types={types}
          onClose={() => setEditDoc(null)}
          onSaved={async () => {
            setEditDoc(null);
            if (child) await load(child.id);
          }}
        />
      )}

      <ConfirmationModal
        show={!!deleteDoc}
        title="Delete document"
        message={`Delete "${deleteDoc?.display_name ?? ''}"? This cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDoc(null)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

// --- Edit metadata modal (Admin only) -------------------------------------

const EditDocumentModal: React.FC<{
  doc: ChildDocument;
  types: DocumentType[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ doc, types, onClose, onSaved }) => {
  const [displayName, setDisplayName] = useState(doc.display_name);
  const [description, setDescription] = useState(doc.description ?? '');
  const [documentDate, setDocumentDate] = useState(doc.document_date ?? '');
  const [documentTypeId, setDocumentTypeId] = useState<number>(doc.document_type_id);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!displayName.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const payload: ChildDocumentMetadata = {
        display_name: displayName.trim(),
        description: description.trim() || null,
        document_date: documentDate || null,
        document_type_id: documentTypeId,
      };
      await ChildDocumentDataService.update(doc.id, payload);
      onSaved();
    } catch (e) {
      console.error('Failed to save document', e);
      setErr('Failed to save changes.');
      setSaving(false);
    }
  };

  return (
    <div className="cd-modal-backdrop" onClick={onClose}>
      <div className="cd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cd-modal__head">
          <div className="cd-modal__heading">
            <span className="cd-modal__eyebrow">Edit document</span>
            <h3 className="cd-modal__title" title={doc.display_name}>{doc.display_name}</h3>
          </div>
          <button type="button" className="cd-modal__close" onClick={onClose} title="Close" aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="cd-modal__body">
          {err && <div className="cd-alert"><i className="fas fa-triangle-exclamation" />{err}</div>}

          <div className="cd-form-row">
            <label className="cd-form-label">Name</label>
            <input
              className="cd-input"
              value={displayName}
              autoFocus
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="cd-form-grid">
            <div className="cd-form-row">
              <label className="cd-form-label">Type</label>
              <select className="cd-input" value={documentTypeId} onChange={(e) => setDocumentTypeId(Number(e.target.value))}>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="cd-form-row">
              <label className="cd-form-label">Document date</label>
              <input type="date" className="cd-input" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
            </div>
          </div>

          <div className="cd-form-row">
            <label className="cd-form-label">Description</label>
            <textarea className="cd-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>

        <div className="cd-modal__foot">
          <button type="button" className="cd-btn cd-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="cd-btn cd-btn--primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChildDocuments;
